import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AssetKindSchema,
  reanalysisGaveUp,
  uploadAbandoned,
  type AssetKind,
  type ImportMode,
} from "@scenix/core";
import { ApiError, api } from "../lib/api";
import { sourceLabel } from "../lib/assetSource";
import { isYouTubeAddress, nameForAddress } from "../lib/sourcePlatform";
import { RECOVERY_WINDOW_DAYS } from "../lib/recovery";
import { RecoveryCountdown } from "../components/RecoveryCountdown";
import { SourceLink } from "../components/SourceLink";
import { searchable } from "../lib/search";
import { UploadCancelled, putObject } from "../lib/upload";
import { Crumbs } from "../components/Crumbs";

/**
 * The corpus in a space: everything that has been brought in, and the two ways
 * to bring more (ADR 000028).
 */
// Shared with the command palette: the same box in two places should wait the
// same amount and refuse to search on a single character in both.
const contentSearchDebounceMs = 200;
// How long a finished batch stays on screen before the successful rows go. Long
// enough to read "done" against the file you chose, short enough that the list
// is not still there when you next look at the page.
const clearDownMs = 2500;
// How long an upload may send nothing before the row says it has stalled.
// Deliberately generous: a connection that pauses for half a minute under load
// has not failed, and calling it stalled would be a false alarm on exactly the
// slow link this is meant to help (ADR 000083).
const stallAfterMs = 60_000;
// How often that is asked. The answer only changes on the minute boundary
// above, so anything finer is re-rendering the list to say the same thing.
const stallCheckMs = 5_000;

interface AssetsRouteProps {
  /**
   * How long to wait after a keystroke before asking the server, in
   * milliseconds.
   *
   * A seam, not a setting. Nothing in the application passes it: the default is
   * the behaviour. It exists because three separate tests in this route have
   * failed on CI waiting for this delay plus a round trip to fit inside
   * testing-library's default second, each time reading as the search being
   * broken rather than the clock being tight. A test that sets it to zero is
   * asserting what the search does rather than how fast the runner is.
   */
  debounceMs?: number;
}

export function AssetsRoute({ debounceMs = contentSearchDebounceMs }: AssetsRouteProps = {}) {
  const { spaceId = "" } = useParams();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<AssetKind | "">("");
  const [query, setQuery] = useState("");
  // Debounced separately from the box itself, so typing does not fire a
  // manifest-content search per keystroke. The same shape the command palette
  // already uses, and the same two constants.
  const [contentQuery, setContentQuery] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  // The recovery window (issue #8) is a different mode of this page rather
  // than a filter alongside kind: what it shows is not addressable the normal
  // way (its detail page 404s until restored), so it gets its own list and
  // its own actions instead of a Link into content that is not there.
  const [showDeleted, setShowDeleted] = useState(false);

  // "paged" is in the key because what lives under it is react-query's paged
  // shape, {pages, pageParams}, and not a list of assets.
  //
  // Six other places read a space's corpus with a plain useQuery and walk the
  // answer: the palette, the thread and timeline panels, an asset's own page
  // and the space landing page. A cache entry is keyed, not typed, so before
  // this segment existed the first of those to read ["assets", spaceId, ""]
  // after this page had filled it got the envelope instead of the array and
  // threw "object is not iterable" on the next line. It survived review and
  // twelve days on main because every one of those components is tested with a
  // query client of its own, where the collision cannot happen.
  //
  // Still under ["assets", spaceId], so the one invalidation that clears this
  // page still clears it.
  const assetsKey = ["assets", spaceId, "paged", kind];

  // One page of the corpus. Written once because the listing on screen and the
  // count beside the kind buttons are the same request when no kind is chosen,
  // and they have to stay the same shape to share a cache entry.
  const corpusPage = (forKind: AssetKind | "", after: string) =>
    api.listAssetPage(spaceId as never, {
      ...(forKind ? { kind: forKind } : {}),
      ...(after ? { after } : {}),
    });

  // Paged rather than fetched whole, because the listing is capped and a space
  // accumulates: without this the oldest material in a space past the cap is
  // not on this page, and there is nowhere else an asset id is handed to a
  // person (issue #353, ADR 000093).
  const assets = useInfiniteQuery({
    queryKey: assetsKey,
    queryFn: ({ pageParam }) => corpusPage(kind, pageParam),
    initialPageParam: "",
    // Empty means the corpus ends here. undefined is how react-query is told
    // there is no next page, and "" is not the same thing to it.
    getNextPageParam: (page) => page.nextCursor || undefined,
    // Ingest is asynchronous, so a page left open should catch up on its own
    // rather than needing a reload to notice something finished. It costs a
    // request per page somebody has loaded, and only while something on one of
    // them is still moving: a corpus at rest asks for nothing.
    refetchInterval: (query) =>
      (query.state.data?.pages ?? []).some((page) =>
        page.assets.some((asset) => !isSettled(asset.status) && !uploadAbandoned(asset)),
      )
        ? 4000
        : false,
    enabled: !showDeleted,
  });

  // The corpus with no kind applied, purely to count what exists. The listing
  // above is fetched with the chosen kind, so counting from it makes every
  // other kind read zero and disables the button that would leave the filter
  // (issue #157). Cached under its own key, so switching kinds re-reads
  // nothing, and that key is the listing's own when no kind is chosen, so the
  // two are one request rather than two identical ones.
  //
  // Nothing pages it on its own, so in a space past the cap it holds an
  // undercount, which is the cost ADR 000093 names rather than hides. Counting
  // properly is an aggregation query per kind, not a listing. Until there is
  // one, an undercount is said to be one rather than shown as a total, and it
  // never takes a filter away (ADR 000119).
  const everyKind = useInfiniteQuery({
    queryKey: ["assets", spaceId, "paged", ""],
    queryFn: ({ pageParam }) => corpusPage("", pageParam),
    initialPageParam: "",
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled: !showDeleted,
  });

  useEffect(() => {
    const id = setTimeout(() => setContentQuery(query.trim()), debounceMs);
    return () => clearTimeout(id);
  }, [query, debounceMs]);

  // The half a client-side filter cannot do: a word said in a video is in the
  // manifest, never in this listing, so matching it is the term index the API
  // queries (ADR 000045).
  //
  // The kind travels with the query. Without it this half answered over the
  // whole space while the local half answered over one kind, so typing a word
  // put back the kinds the toolbar had just excluded (issue #149).
  const contentMatches = useQuery({
    queryKey: ["assets-search", spaceId, kind, contentQuery],
    queryFn: () =>
      api.listAssets(spaceId as never, kind ? { kind, q: contentQuery } : { q: contentQuery }),
    // Not asked at all when the index could not answer it: typing "ai"
    // falls back to the name match below rather than refusing mid-word
    // (issue #178).
    enabled: !showDeleted && searchable(contentQuery),
  });

  // Paged like the listing it sits behind, because this is the one screen where
  // the cap and the end of the list are not the same thing. A bulk delete puts
  // the oldest deletions behind the first page, and the store returns newest
  // first, so those are the ones nearest the sweep that empties them for good:
  // unreachable exactly while somebody still had the time to undo it
  // (issue #467, ADR 000055, ADR 000093).
  const deletedAssets = useInfiniteQuery({
    queryKey: ["assets", spaceId, "paged", "deleted"],
    queryFn: ({ pageParam }) =>
      api.listAssetPage(spaceId as never, {
        deleted: true,
        ...(pageParam ? { after: pageParam } : {}),
      }),
    initialPageParam: "",
    // Empty means the window ends here, and undefined is how react-query is
    // told there is no next page, the same as the corpus listing above.
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled: showDeleted,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["assets", spaceId] });
  };

  const restore = useMutation({
    mutationFn: (assetId: string) => api.restoreAsset(spaceId as never, assetId as never),
    onSuccess: refresh,
  });

  const { uploads, send, cancel, busy } = useUploads(spaceId, refresh);
  const [dropping, setDropping] = useState(false);

  if (showDeleted) {
    // Every page of the window asked for so far, which is what is on screen and
    // what "Load older deletions" grows.
    const restorable = deletedAssets.data?.pages.flatMap((page) => page.assets) ?? [];

    return (
      <section>
        <header className="page-head">
          <Crumbs spaceId={spaceId} />
          <h1>Deleted</h1>
          <p className="muted">
            Restore brings something back exactly as it was, for {RECOVERY_WINDOW_DAYS} days after
            it was deleted. After that it is removed for good, bytes and all, and nothing here can
            bring it back.
          </p>
        </header>
        <button type="button" className="ghost" onClick={() => setShowDeleted(false)}>
          ← Back to content
        </button>
        {/* A refused restore is not an ordinary refused write: the sweep empties
            the window on its own clock whether or not anybody managed to press
            the button, so somebody told nothing has no reason to come back and
            try tomorrow, and no tomorrow to come back to (ADR 000055). Read off
            the mutation rather than through this route's own `failure`, which
            an import already writes to and which is rendered past the return
            below, out of reach of this screen. */}
        {restore.error && (
          <p role="alert" className="error">
            {describe(restore.error)}
          </p>
        )}
        {deletedAssets.isPending ? (
          <p role="status">Loading…</p>
        ) : deletedAssets.error ? (
          <p className="error">{describe(deletedAssets.error)}</p>
        ) : restorable.length === 0 ? (
          <div className="empty-inline">
            <p>Nothing deleted right now.</p>
          </div>
        ) : (
          <ul className="corpus">
            {restorable.map((asset) => (
              <li key={asset.id} className="corpus-item">
                <div className="corpus-item-row">
                  <span className="corpus-thumb" aria-hidden="true">
                    {iconFor(asset.kind)}
                  </span>
                  <span className="corpus-body">
                    <strong>{asset.name}</strong>
                    <span className="corpus-meta">
                      {asset.kind}
                      {asset.deletedAt &&
                        ` · deleted ${new Date(asset.deletedAt).toLocaleDateString()}`}
                    </span>
                    <RecoveryCountdown deletedAt={asset.deletedAt} />
                  </span>
                  <button
                    type="button"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(asset.id)}
                  >
                    Restore
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* A list that ends reads as a complete one, and here that reading is
            expensive: what is missing is not older material somebody can find
            another way, it is the deletions with the least of their thirty days
            left, and there is no other door to them (issue #467). */}
        {deletedAssets.hasNextPage && (
          <div className="load-more">
            <button
              type="button"
              className="ghost"
              disabled={deletedAssets.isFetchingNextPage}
              onClick={() => void deletedAssets.fetchNextPage()}
            >
              {deletedAssets.isFetchingNextPage ? "Loading…" : "Load older deletions"}
            </button>
          </div>
        )}
      </section>
    );
  }

  if (assets.isPending) return <p role="status">Loading…</p>;
  if (assets.error) return <p className="error">{describe(assets.error)}</p>;

  // Every page asked for so far, which is what is on screen and what "Load
  // older content" grows (ADR 000093).
  const loaded = assets.data.pages.flatMap((page) => page.assets);

  // Two searches, because they answer different questions. Matching a name is
  // instant and local: the assets are already loaded and a round trip per
  // keystroke would be slower and worse. Matching what was *said* in a video
  // cannot be, because a transcript is in the manifest and never in this
  // response, so that half is the term index the API queries (ADR 000045).
  //
  // Without the second one this box quietly searched titles while the command
  // palette searched the corpus, and the one with the magnifying glass in it
  // was the weaker of the two (issue #117).
  //
  // The local half only sees what has been paged in, and since ADR 000094 the
  // index carries names too, so it is a head start rather than the only way to
  // match one: a title on a page nobody has loaded is found by the half that
  // goes to the server.
  const needle = query.trim().toLowerCase();
  const byName = needle
    ? loaded.filter((asset) =>
        [asset.name, asset.summary, ...asset.topics].join(" ").toLowerCase().includes(needle),
      )
    : loaded;

  const byContent = contentMatches.data ?? [];
  const seen = new Set(byName.map((asset) => asset.id));
  // Name matches first: somebody who typed a title meant that asset, and a
  // transcript mention of the same word is the weaker signal.
  const shown = needle
    ? [...byName, ...byContent.filter((asset) => !seen.has(asset.id) && !asset.deletedAt)]
    : loaded;

  // Counts per kind, so a filter that would show nothing says so before it is
  // clicked. Counted over everything the space holds rather than over what the
  // search or the chosen kind left: the numbers must not move as somebody
  // types, and a kind must stay reachable once another one is picked.
  const counted = everyKind.data?.pages.flatMap((page) => page.assets) ?? loaded;
  const counts = new Map<string, number>();
  for (const asset of counted) {
    counts.set(asset.kind, (counts.get(asset.kind) ?? 0) + 1);
  }

  // Whether there is corpus behind what was counted. A cursor on the last page
  // says there is, and every number below is then a floor rather than a total
  // (ADR 000119). It grows as somebody pages, because the unfiltered listing
  // and these counts are one cache entry while no kind is chosen.
  const partialCounts = everyKind.hasNextPage;
  // "12" is a count; "12+" is at least twelve and we cannot say how many more.
  const countLabel = (total: number) => (partialCounts ? `${total}+` : `${total}`);

  return (
    <section
      // Dropped anywhere on the page, not only on a target somebody has to
      // find. The counter guards against dragenter firing for every child.
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) {
          event.preventDefault();
          setDropping(true);
        }
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropping(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDropping(false);
        send([...event.dataTransfer.files]);
      }}
    >
      {dropping && (
        <div className="drop-veil" aria-hidden="true">
          <span>Drop to add to this space</span>
        </div>
      )}
      <header className="page-head">
        <Crumbs spaceId={spaceId} />
        <h1>Content</h1>
        <p className="muted">
          Everything this space draws on. Footage, stills, audio, research and links, all analysed
          on the way in.
        </p>
        <button type="button" className="ghost" onClick={() => setShowDeleted(true)}>
          Deleted
        </button>
      </header>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      <div className="ingest">
        <UploadForm spaceId={spaceId} uploads={uploads} send={send} cancel={cancel} busy={busy} />
        <ImportForm spaceId={spaceId} onDone={refresh} onFailure={setFailure} />
      </div>

      <div className="toolbar">
        <div className="search">
          <svg viewBox="0 0 16 16" aria-hidden="true" className="search-icon">
            <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path
              d="M10.5 10.5 14 14"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            value={query}
            aria-label="Search this space"
            placeholder="Search names, summaries and topics"
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button
              type="button"
              className="search-clear"
              aria-label="Clear search"
              onClick={() => setQuery("")}
            >
              ×
            </button>
          )}
        </div>

        <div className="filters">
          <button type="button" className={kind === "" ? "" : "ghost"} onClick={() => setKind("")}>
            All <span className="count">{countLabel(counted.length)}</span>
          </button>
          {/* Every kind there is, so a sixth arrives with a button of its own:
              a row of buttons reads as the complete set (issue #358). */}
          {AssetKindSchema.options.map((option) => (
            <button
              key={option}
              type="button"
              className={kind === option ? "" : "ghost"}
              // Nothing of this kind is here, so say so rather than offering a
              // filter that empties the page. Only where the count is a total:
              // a kind absent from the newest page may fill the year behind it,
              // and the filter is the only way to reach it (ADR 000119).
              disabled={!partialCounts && !counts.has(option) && kind !== option}
              onClick={() => setKind(option)}
            >
              {option} <span className="count">{countLabel(counts.get(option) ?? 0)}</span>
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="empty-inline">
          {needle ? (
            <>
              <p>
                Nothing matches <strong>{query}</strong>.
              </p>
              <button type="button" className="ghost" onClick={() => setQuery("")}>
                Clear search
              </button>
            </>
          ) : kind ? (
            <p>Nothing of that kind here yet.</p>
          ) : (
            <>
              <p>Nothing here yet.</p>
              <p className="muted">Drop a file anywhere on this page, or paste a link above.</p>
            </>
          )}
        </div>
      ) : (
        <ul className="corpus">
          {shown.map((asset) => (
            <li
              key={asset.id}
              className={`corpus-item is-${uploadAbandoned(asset) ? "abandoned" : asset.status}`}
            >
              <Link className="corpus-row" to={`/spaces/${spaceId}/content/${asset.id}`}>
                {asset.thumbnailPath ? (
                  <img
                    className="corpus-thumb"
                    src={api.assetThumbnailUrl(spaceId as never, asset.id, asset.thumbnailPath)}
                    loading="lazy"
                    alt=""
                  />
                ) : (
                  <span className="corpus-thumb" aria-hidden="true">
                    {iconFor(asset.kind)}
                  </span>
                )}
                <span className="corpus-body">
                  <strong>
                    {asset.name}
                    {/* Called out beyond the meta line, so a model's guess does not
                        read as a primary source at a glance (ADR 000037). */}
                    {asset.source === "generated" && (
                      <span className="source-badge">Generated</span>
                    )}
                  </strong>
                  <span className="corpus-meta">
                    {asset.kind}
                    {` · ${sourceLabel(asset.source)}`}
                    {asset.durationSec > 0 && ` · ${duration(asset.durationSec)}`}
                    {asset.importMode && ` · ${asset.importMode}`}
                    {/* An abandoned upload is still "pending" on the record, and
                        saying so would make it indistinguishable from one going
                        perfectly, which is the whole complaint (ADR 000054). */}
                    {uploadAbandoned(asset) ? (
                      <span className="corpus-abandoned"> · upload never finished</span>
                    ) : (
                      !isSettled(asset.status) && ` · ${asset.status}…`
                    )}
                    {asset.status === "failed" && ` · failed`}
                    {/* Four words and not the reason: this is how the eleven
                        assets a space-wide pass gave up on are found among the
                        hundred it was asked about, and the reason itself is a
                        paragraph of engine error that belongs on the asset's
                        own page (ADR 000120). */}
                    {reanalysisGaveUp(asset) && (
                      <span className="corpus-gave-up"> · re-analysis gave up</span>
                    )}
                  </span>
                  {/*
                   * The summary is denormalised onto the asset for exactly this,
                   * so a listing never opens a manifest (ADR 000028).
                   */}
                  <span className="corpus-summary">
                    {asset.summary || (
                      <span className="muted">
                        {uploadAbandoned(asset)
                          ? "The upload stopped before the file arrived, and cannot be resumed. Remove this and drag the file again."
                          : asset.status === "failed"
                            ? asset.failureReason || "Analysis failed."
                            : "Not analysed yet."}
                      </span>
                    )}
                  </span>
                  {asset.topics.length > 0 && (
                    <span className="corpus-topics">
                      {asset.topics.slice(0, 6).map((topic) => (
                        <span key={topic}>{topic}</span>
                      ))}
                    </span>
                  )}
                </span>
              </Link>
              {/* Outside the row's own link rather than inside it: an anchor
                  nested in an anchor is invalid, and a click would follow
                  both. */}
              <SourceLink sourceUrl={asset.sourceUrl} />
            </li>
          ))}
        </ul>
      )}

      {/* The corpus does not end where this page does, and saying so is half
          the fix: a space that had quietly lost its first three months of
          footage looked exactly like a space holding two hundred assets
          (issue #353). Hidden while a search is on screen, because what is
          shown then is a merge of a name match over what is loaded and a
          content match from the server, and paging the listing underneath it
          would not be what the button appeared to offer. */}
      {!needle && assets.hasNextPage && (
        <div className="load-more">
          <button
            type="button"
            className="ghost"
            disabled={assets.isFetchingNextPage}
            onClick={() => void assets.fetchNextPage()}
          >
            {assets.isFetchingNextPage ? "Loading…" : "Load older content"}
          </button>
        </div>
      )}
    </section>
  );
}

/** What one file is doing, so several can be shown at once. */
interface Upload {
  // Its own identity rather than its name, because two batches on screen
  // together can each hold a file called clip.mp4, and a row belongs to the
  // batch that made it (issue #146).
  id: number;
  name: string;
  state: "waiting" | "sending" | "analysing" | "done" | "failed" | "cancelled";
  detail?: string;
  /** Bytes acknowledged as sent, while sending (ADR 000083). */
  sent?: number;
  /** The file's own size, so a percentage can be worked out. */
  total?: number;
  /** When `sent` last moved, which is what stalling is measured from. */
  movedAt?: number;
  /** Nothing has gone for `stallAfterMs`. Only meaningful while sending. */
  stalled?: boolean;
}

/**
 * Uploading, by picker or by dropping files anywhere on the page.
 *
 * Several at a time: choosing files one at a time is the wrong shape for a
 * corpus, and a media tool that cannot accept a dropped file is missing the
 * gesture everybody tries first.
 */
function useUploads(spaceId: string, onDone: () => void) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  // The clear-down below outlives the batch that scheduled it, so it is held
  // rather than fired and forgotten. Left loose it belongs to nobody: a second
  // batch chosen inside the window is emptied off the screen by the first
  // batch's timer while it is still uploading, and leaving the page inside the
  // window sets state on a component that is gone.
  const clearDown = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // How many loops are still going. Dropping a second batch mid-upload is what
  // filling a corpus looks like rather than a mistake, so the list is shared
  // and the last loop to leave is the one that may tidy it away.
  const running = useRef(0);
  const nextId = useRef(0);
  // One per row, from the moment the row appears rather than from the moment
  // its PUT starts: the loop below is sequential, so a file can sit behind a
  // 40 GB one and be called off before a byte of it is sent (ADR 000083).
  const controllers = useRef(new Map<number, AbortController>());

  useEffect(() => () => clearTimeout(clearDown.current), []);

  const update = (id: number, change: Partial<Upload>) =>
    setUploads((all) => all.map((u) => (u.id === id ? { ...u, ...change } : u)));

  // A count that is moving is only legible to somebody already watching it, and
  // the reason to know the count at all is to find out that it stopped without
  // having to (ADR 000083). Only while something is sending, so the page
  // schedules nothing of its own once a batch is over or the page is left.
  const sending = uploads.some((u) => u.state === "sending");
  useEffect(() => {
    if (!sending) return;
    const tick = setInterval(() => {
      const cutoff = Date.now() - stallAfterMs;
      setUploads((all) => {
        let moved = false;
        const next = all.map((u) => {
          if (u.state !== "sending" || u.movedAt === undefined) return u;
          const stalled = u.movedAt < cutoff;
          if (stalled === (u.stalled ?? false)) return u;
          moved = true;
          return { ...u, stalled };
        });
        // Returning the same array leaves the list identical, so a tick with
        // nothing to say does not re-render every row it looked at.
        return moved ? next : all;
      });
    }, stallCheckMs);
    return () => clearInterval(tick);
  }, [sending]);

  const cancel = (id: number) => controllers.current.get(id)?.abort();

  const send = async (files: File[]) => {
    if (files.length === 0) return;
    clearTimeout(clearDown.current);

    // Appended rather than replacing what is on screen. Replacing dropped the
    // rows of a batch that was still uploading, so bytes went up for files a
    // person could no longer see, with nothing saying whether they were still
    // going, failed, or never accepted at all (issue #146).
    const batch = files.map((file) => ({
      file,
      controller: new AbortController(),
      row: { id: nextId.current++, name: file.name, state: "waiting" as const },
    }));
    for (const { row, controller } of batch) controllers.current.set(row.id, controller);
    setUploads((all) => [...all, ...batch.map(({ row }) => row)]);

    running.current += 1;
    try {
      // One at a time. In parallel, a dozen large files compete for the same
      // uplink and every one of them finishes late.
      for (const { file, row, controller } of batch) {
        const { signal } = controller;
        let created: Awaited<ReturnType<typeof api.createAsset>> | undefined;
        try {
          // Called off while it waited its turn, so nothing was ever made for
          // it and there is nothing to clean up.
          if (signal.aborted) throw new UploadCancelled();

          update(row.id, { state: "sending", sent: 0, total: file.size, movedAt: Date.now() });
          created = await api.createAsset(spaceId as never, {
            kind: kindForFile(file),
            source: "upload",
            name: file.name,
            filename: file.name,
            contentType: file.type || "application/octet-stream",
          });

          // Straight to storage. The API never sees the bytes, which is what
          // keeps an instance from being a proxy for a 40 GB master
          // (ADR 000028).
          await putObject({
            url: created.uploadUrl,
            body: file,
            contentType: file.type || "application/octet-stream",
            signal,
            onProgress: ({ sent, total }) =>
              update(row.id, { sent, total, movedAt: Date.now(), stalled: false }),
          });

          // The bytes are up, and a cancel that landed during the last of them
          // still means the person does not want this asset. Taking the same
          // path rather than racing the response keeps "cancel" one answer
          // instead of two that depend on the moment (ADR 000083).
          if (signal.aborted) throw new UploadCancelled();

          update(row.id, { state: "analysing" });
          await api.completeUpload(spaceId as never, created.asset.id);
          update(row.id, { state: "done" });
        } catch (error) {
          // createAsset already made a record before the PUT or the complete
          // call failed. Left alone, it sits in the corpus stuck at "pending"
          // forever, with no failure reason to explain it apart from a real
          // upload still in flight, and no way back short of an admin finding
          // it by hand: exactly the "retried or swept" case ADR 000028 names.
          // Sweeping it here, while the file that would have filled it is
          // still in hand, is simplest, and a cancel is the same situation
          // arrived at deliberately (ADR 000083).
          if (created) {
            await api.deleteAsset(spaceId as never, created.asset.id).catch(() => {});
          }
          // One bad file does not stop the rest: the others are already chosen
          // and re-picking them is the worst possible recovery. A cancelled one
          // is the same: stopping this file is not stopping the batch.
          update(
            row.id,
            error instanceof UploadCancelled
              ? { state: "cancelled" }
              : { state: "failed", detail: describe(error) },
          );
        } finally {
          controllers.current.delete(row.id);
        }
        onDone();
      }
    } finally {
      running.current -= 1;
      // Successful ones disappear, and so do cancelled ones: somebody who
      // stopped an upload does not need to be told about it afterwards.
      // Failures stay until they are read, because nobody asked for one. Only
      // once every batch has finished, because a list two loops filled is not
      // done when the first of them is, and clearing it then takes rows off
      // the screen for files still going up. In the finally, so a loop that
      // dies unexpectedly still hands the list on rather than leaving it.
      if (running.current === 0) {
        clearDown.current = setTimeout(
          () => setUploads((all) => all.filter((u) => u.state === "failed")),
          clearDownMs,
        );
      }
    }
  };

  return {
    uploads,
    send,
    cancel,
    busy: sending || uploads.some((u) => u.state === "analysing"),
  };
}

function UploadForm({
  spaceId,
  uploads,
  send,
  cancel,
  busy,
}: {
  spaceId: string;
  uploads: Upload[];
  send: (files: File[]) => void;
  cancel: (id: number) => void;
  busy: boolean;
}) {
  void spaceId;

  return (
    <div className="ingest-card">
      <h2>Upload</h2>
      <p className="muted">
        Video, audio, a still, or research: a PDF, a transcript, notes. Drop files anywhere on this
        page.
      </p>

      <label className="file-button">
        <input
          type="file"
          multiple
          aria-label="Files to upload"
          onChange={(event) => {
            send([...(event.target.files ?? [])]);
            // Cleared so choosing the same file twice still fires a change.
            event.target.value = "";
          }}
        />
        <span>{busy ? "Uploading…" : "Choose files"}</span>
      </label>

      {uploads.length > 0 && (
        <ul className="uploads">
          {uploads.map((upload) => (
            <li key={upload.id} className={classOf(upload)}>
              <span className="upload-name">{upload.name}</span>
              <span className="upload-state">{stateOf(upload)}</span>
              {/* Only while there is something to stop. Once the bytes are up,
                  completing takes a moment and there is nothing left to call
                  off (ADR 000083). */}
              {(upload.state === "waiting" || upload.state === "sending") && (
                <button
                  type="button"
                  className="ghost"
                  aria-label={`Cancel uploading ${upload.name}`}
                  onClick={() => cancel(upload.id)}
                >
                  Cancel
                </button>
              )}
              {upload.state === "sending" && upload.total ? (
                <progress className="upload-bar" value={upload.sent ?? 0} max={upload.total} />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const LABELS: Record<Upload["state"], string> = {
  waiting: "waiting",
  sending: "sending",
  analysing: "analysing",
  done: "done",
  failed: "failed",
  cancelled: "cancelled",
};

/**
 * How one row is drawn.
 *
 * Stalling is read off the state as well as the flag, so a row that stalled and
 * then finished is not still coloured as a problem.
 */
function classOf(upload: Upload): string {
  const stalled = upload.state === "sending" && upload.stalled;
  return `is-${upload.state}${stalled ? " is-stalled" : ""}`;
}

/**
 * What one row says it is doing.
 *
 * A transfer says how much of it has gone, in bytes as well as a percentage:
 * 4% is a different fact depending on whether the file is 200 MB or 40 GB, and
 * the second number is the one that says whether waiting is reasonable
 * (ADR 000083). Before the first progress event there is nothing yet to report,
 * so it reads the way it always did.
 */
function stateOf(upload: Upload): string {
  if (upload.state === "failed") return upload.detail ?? "failed";
  if (upload.state !== "sending") return LABELS[upload.state];

  // Before the first progress event nothing has been measured, and "0 B of
  // 40 GB (0%)" claims a measurement rather than admitting there is not one
  // yet. An upload that never reports a byte is exactly the stalled case, so
  // that is still said.
  const progress =
    upload.sent && upload.total
      ? `${formatBytes(upload.sent)} of ${formatBytes(upload.total)} (${Math.floor(
          (upload.sent / upload.total) * 100,
        )}%)`
      : "";
  if (upload.stalled) return progress ? `stalled at ${progress}` : "stalled";
  return progress || LABELS.sending;
}

/**
 * Bytes, at the scale a person reads them.
 *
 * Powers of ten rather than of two, because that is what the operating system
 * showed them when they picked the file and disagreeing by 7% is worse than
 * being pedantically right.
 */
function formatBytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} kB`;
  return `${Math.round(bytes)} B`;
}

/**
 * An import names how much to take, and the default takes the least: audio,
 * transcribed, keeping no media (ADR 000028).
 */
function ImportForm({
  spaceId,
  onDone,
  onFailure,
}: {
  spaceId: string;
  onDone: () => void;
  onFailure: (message: string | null) => void;
}) {
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState<ImportMode>("transcribe");

  const importing = useMutation({
    mutationFn: (sourceUrl: string) => {
      // The one question this form asks about the address, asked once, and
      // answered by the list the API will answer it from (ADR 000124).
      const youtube = isYouTubeAddress(sourceUrl);
      return api.createAsset(spaceId as never, {
        kind: youtube ? "video" : "link",
        source: youtube ? "youtube" : "link",
        // Nobody typed a name, so the form makes one that fits. Sending the
        // address whole refused every import over 300 characters, for a field
        // the form never asked about (#488).
        name: nameForAddress(sourceUrl),
        sourceUrl,
        ...(youtube ? { importMode: mode } : {}),
      });
    },
    onSuccess: () => {
      setUrl("");
      onFailure(null);
      onDone();
    },
    onError: (error) => onFailure(describe(error)),
  });

  const youtube = isYouTubeAddress(url);

  return (
    <form
      className="ingest-card"
      onSubmit={(event) => {
        event.preventDefault();
        if (url.trim()) importing.mutate(url.trim());
      }}
    >
      <h2>Import</h2>
      <p className="muted">A YouTube video, or any link worth keeping.</p>
      <input
        type="url"
        value={url}
        aria-label="Address to import"
        placeholder="https://www.youtube.com/watch?v=…"
        onChange={(event) => setUrl(event.target.value)}
      />

      {/* What the row will say, before it says it. The name is derived rather
          than asked for, and a derived name nobody is shown is one nobody
          knows to correct (ADR 000060). */}
      {url.trim() && <p className="muted">Listed as {nameForAddress(url)}</p>}

      {youtube && (
        <fieldset className="modes">
          <legend>How much to take</legend>
          {(
            [
              ["transcribe", "Transcript only", "Keeps no media. The words are the point."],
              ["audio", "Audio", "For interviews, music, anything heard."],
              ["full", "Video", "For footage meant for a cut."],
            ] as const
          ).map(([value, label, why]) => (
            <label key={value}>
              <input
                type="radio"
                name="importMode"
                value={value}
                checked={mode === value}
                onChange={() => setMode(value)}
              />
              <span>
                <strong>{label}</strong>
                <span className="muted">{why}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}

      <button type="submit" disabled={!url.trim() || importing.isPending}>
        {importing.isPending ? "Importing…" : "Import"}
      </button>
    </form>
  );
}

/**
 * The kind a file is, from its type. A guess the server does not have to trust:
 * it is what the browser reports, and analysis reads the actual container.
 */
function kindForFile(file: File): AssetKind {
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type.startsWith("image/")) return "image";
  return "document";
}

function isSettled(status: string): boolean {
  return status === "ready" || status === "failed";
}

function iconFor(kind: AssetKind): string {
  switch (kind) {
    case "video":
      return "▶";
    case "audio":
      return "♪";
    case "image":
      return "▣";
    case "link":
      return "↗";
    default:
      return "≡";
  }
}

function duration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds - minutes * 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
